import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import StatusBadge from "../components/StatusBadge";
import { api } from "../lib/api";
import { Users, Clock, Calendar, TrendingUp } from "lucide-react";

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/applications"),
        ]).then(([s, a]) => {
            setStats(s.data);
            setRecent(a.data.slice(0, 6));
        }).catch(() => { });
    }, []);

    const cards = [
        { label: "Total Members", value: stats?.total_members ?? 0, icon: Users, testId: "stat-card-members", accent: "bg-[#0F172A] text-[#D4AF37]" },
        { label: "Pending Applications", value: stats?.pending_applications ?? 0, icon: Clock, testId: "stat-card-pending", accent: "bg-yellow-50 text-yellow-700 border border-yellow-200" },
        { label: "Upcoming Events", value: stats?.upcoming_events ?? 0, icon: Calendar, testId: "stat-card-events", accent: "bg-blue-50 text-blue-700 border border-blue-200" },
        { label: "Event Registrations", value: stats?.total_registrations ?? 0, icon: TrendingUp, testId: "stat-card-registrations", accent: "bg-green-50 text-green-700 border border-green-200" },
    ];

    return (
        <AdminLayout>
            <div className="mb-10" data-testid="admin-dashboard">
                <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-2">Federation Overview</div>
                <h1 className="font-display text-3xl font-bold text-[#0F172A] tracking-tight">Dashboard</h1>
            </div>

            {/* Stat cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0] mb-10">
                {cards.map((c) => (
                    <div key={c.label} data-testid={c.testId} className="bg-white p-6">
                        <div className="flex items-start justify-between mb-6">
                            <div className={`w-10 h-10 flex items-center justify-center ${c.accent}`}>
                                <c.icon className="w-5 h-5" />
                            </div>
                        </div>
                        <div className="font-display text-3xl font-bold text-[#0F172A] tabular">{c.value}</div>
                        <div className="text-xs uppercase tracking-[0.12em] text-[#64748B] mt-2">{c.label}</div>
                    </div>
                ))}
            </div>

            {/* Two columns */}
            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 bg-white border border-[#E2E8F0]">
                    <div className="flex items-center justify-between px-6 py-5 border-b border-[#E2E8F0]">
                        <h2 className="font-display text-lg font-bold text-[#0F172A]">Recent Applications</h2>
                        <Link to="/admin/applications" data-testid="view-all-applications" className="text-xs uppercase tracking-[0.12em] text-[#D4AF37] hover:text-[#B4952D] font-semibold">View all →</Link>
                    </div>
                    {recent.length === 0 ? (
                        <div className="p-10 text-center text-sm text-[#64748B]">No applications yet</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-[#64748B] bg-[#F8F9FA]">
                                <tr>
                                    <th className="text-left px-6 py-3 font-medium">Application</th>
                                    <th className="text-left px-6 py-3 font-medium">Company</th>
                                    <th className="text-left px-6 py-3 font-medium">Category</th>
                                    <th className="text-left px-6 py-3 font-medium">Status</th>
                                    <th className="text-left px-6 py-3 font-medium">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E2E8F0]">
                                {recent.map((a) => (
                                    <tr key={a.id} data-testid={`recent-app-${a.id}`} className="hover:bg-[#F8F9FA]">
                                        <td className="px-6 py-4 font-mono text-xs">{a.application_no}</td>
                                        <td className="px-6 py-4 font-medium">{a.company_name}</td>
                                        <td className="px-6 py-4 text-[#64748B]">{a.category}</td>
                                        <td className="px-6 py-4"><StatusBadge status={a.status} /></td>
                                        <td className="px-6 py-4 text-[#64748B]">{formatDate(a.submitted_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="bg-white border border-[#E2E8F0]">
                    <div className="px-6 py-5 border-b border-[#E2E8F0]">
                        <h2 className="font-display text-lg font-bold text-[#0F172A]">Members by Category</h2>
                    </div>
                    <div className="p-6 space-y-4" data-testid="members-by-category">
                        {stats && Object.keys(stats.members_by_category || {}).length === 0 && (
                            <div className="text-sm text-[#64748B]">No approved members yet</div>
                        )}
                        {stats && Object.entries(stats.members_by_category || {}).map(([cat, count]) => {
                            const max = Math.max(...Object.values(stats.members_by_category));
                            const pct = (count / max) * 100;
                            return (
                                <div key={cat}>
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-sm text-[#0F172A] font-medium">{cat}</span>
                                        <span className="text-xs font-mono text-[#64748B]">{count}</span>
                                    </div>
                                    <div className="h-1.5 bg-[#F8F9FA] overflow-hidden">
                                        <div className="h-full bg-[#0F172A]" style={{ width: `${pct}%` }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}
